// Waste & Meals — helpers for the 2026-10-16 additions (sheet retirement):
// the non-loss reason «مرتجع للمستودع» (D2), raw-ingredient items (D2), the
// per-line note cap (A1), the cost snapshot and waste % (D5), and the Excel
// export (A3). Money, day and blob-error helpers are the stock count's own, so
// there is ONE implementation of each.

export { blobErrorMessage, fmt, fmtCost, fmtDay, fmtMoney } from "@/components/stock-count/scUtils";

/** The reason the seed adds for D2. Classification is by `counts_as_waste`;
 *  the key only serves a record whose reason is no longer in the catalogue. */
export const RETURN_REASON_KEY = "returned_to_warehouse";

/** Server cap on a record note (`WM_NOTES_TOO_LONG`, SPEC §2.3). */
export const NOTES_MAX = 300;

/** Export downloads are built in a thread on the server. */
export const WM_EXPORT_TIMEOUT_MS = 120000;

/** A reason without the field IS waste (SPEC §2.2: "a missing field means True") —
 *  except the seeded return reason, in case a response leaves the field out. */
export const countsAsWaste = (reason) => {
    if (reason?.counts_as_waste === undefined || reason?.counts_as_waste === null) {
        return reason?.key !== RETURN_REASON_KEY;
    }
    return reason.counts_as_waste !== false;
};

/** Only the reasons that are a loss — the day sheet's sheet-wide default must
 *  never turn every line into a return (briefs/wm.md §2 trap). */
export const lossReasons = (reasons) => (reasons || []).filter(countsAsWaste);

/** The record's reason is a stock movement (a return), not a loss. */
export function isNonLossReason(reasons, key) {
    if (!key) return false;
    const hit = (reasons || []).find((r) => r.key === key);
    return hit ? !countsAsWaste(hit) : key === RETURN_REASON_KEY;
}

/** A raw-ingredient (stock-count) item, D2. Missing `kind` means a product. */
export const isRawItem = (item) => item?.kind === "stock";

/** A typed note is a note; whitespace is not. */
export const hasText = (v) => !!String(v ?? "").trim();

/** The response carries the field at all (cost is serialised only for report
 *  viewers, so an ABSENT key means "not allowed", a null means "no cost"). */
export const hasKey = (obj, k) => !!obj && Object.prototype.hasOwnProperty.call(obj, k);

/** 3.2 -> «٣٫٢٪» / "3.2%". The API's `*_pct` fields are 0..100, the codebase convention. */
export function fmtPct(v, lang) {
    if (v === null || v === undefined || v === "") return "";
    const n = Number(v);
    if (Number.isNaN(n)) return "";
    return new Intl.NumberFormat(lang === "ar" ? "ar-SA" : "en-US", {
        style: "percent", minimumFractionDigits: 0, maximumFractionDigits: 1,
    }).format(n / 100);
}

/** `/grid` (waste): `waste_pct` (0..100, or null), `waste_pct_through` (the
 *  open month's sales run «حتى <through>», from the daily rollup) and
 *  `waste_pct_state` — ok | no_pos_id | no_sales | no_cost — saying why it is null. */
export function readWastePct(data) {
    if (!data || !hasKey(data, "waste_pct")) return { present: false, pct: null, through: null, state: null };
    const pct = data.waste_pct === undefined ? null : data.waste_pct;
    return { present: true, pct, through: data.waste_pct_through || null, state: data.waste_pct_state || null };
}

/** The i18n key that explains a null waste %. */
export const WASTE_PCT_STATE_KEY = {
    no_pos_id: "wm_grid_waste_pct_no_pos",
    no_sales: "wm_grid_waste_pct_no_sales",
    no_cost: "wm_grid_waste_pct_no_cost",
};
